import { hostWindow } from './host_window'

// Cross-document scrolling, iframe-embed aware.
//
// Widget pages usually live in a same-origin iframe that the host keeps at
// full content height (widgets.init_iframe polls every 300ms and calls the
// host's SetIFrameHeight) — so the frame itself never scrolls and the host
// page owns the scroll. Anything that wants to move the viewport must address
// the top window, offset by the frame's position inside it. Embeds that keep
// their internal scrolling are the exception — their own window is the right
// target: fixed-height embeds (window.glg.widgets.iframe_height) and hosts
// that never grow the iframe at all (multi-widget pages size each column's
// #scroller from the DB and deliberately omit SetIFrameHeight).

// Our frame's offset inside the top viewport, zero when we are not embedded.
export const frameOffset = () => {
  try {
    if (window.frameElement) {
      const rect = window.frameElement.getBoundingClientRect()
      return { top: rect.top, left: rect.left }
    }
  } catch (error) { /* cross-origin parent: treat the frame as the viewport */ }

  return { top: 0, left: 0 }
}

// Embeds sized by the wrapper's fixed-height option scroll internally —
// height syncing and top-window math do not apply to them.
const isFixedHeightEmbed = () =>
  Boolean(window.glg && window.glg.widgets && window.glg.widgets.iframe_height)

// Whether the host grows our iframe to content height — the same
// SetIFrameHeight contract widgets.init_iframe keys its polling off. A
// same-origin host without it (multi-widget pages fix each column's height
// instead) leaves the iframe scrolling internally, so top-window math does
// not apply there.
const hostGrowsIframe = () => {
  try {
    return typeof window.parent.SetIFrameHeight === 'function'
  } catch (error) {
    return false // cross-origin parent: it cannot be our embed wrapper
  }
}

// Content that just mounted makes the host's iframe height stale until its
// next 300ms sync tick, leaving the host page too short to scroll to the new
// content. Resize the iframe up front — same call, same 70px margins as
// widgets.init_iframe — so the full scroll range exists before we measure.
const syncEmbedIframeHeight = () => {
  try {
    if (window.parent === window) return
    if (typeof window.parent.SetIFrameHeight !== 'function') return
    if (isFixedHeightEmbed()) return

    const height = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight)
    window.parent.SetIFrameHeight(height, 70)
  } catch (error) { /* cross-origin parent: not our embed wrapper */ }
}

const fullscreenElement = () =>
  document.fullscreenElement || document.webkitFullscreenElement

// Fraction of the display kept clear above the revealed element when the
// caller does not pick one — the element gets the space below.
const DEFAULT_TOP_RATIO = 0.25

// Native smooth scrolling has a browser-chosen speed that turns long
// distances into a blink, so the reveal animates by hand: eased frames over a
// duration that grows with the distance, inside these bounds.
const MIN_ANIMATION_MS = 400
const MAX_ANIMATION_MS = 800
const ANIMATION_MS_PER_PX = 0.5

// Closer than this the move is imperceptible — jump without animating.
const SKIP_DISTANCE_PX = 24

// A wheel, touch, or scrollbar grab means the user took over — stop steering.
const CANCEL_EVENTS = ['wheel', 'touchstart', 'mousedown']

const easeInOutCubic = (t) =>
  (t < 0.5 ? 4 * t * t * t : 1 - ((-2 * t + 2) ** 3) / 2)

const prefersReducedMotion = () => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  } catch (error) {
    return false
  }
}

// The furthest the window can scroll right now. Sampled every frame as a
// safety net for hosts whose document is still growing mid-animation.
const maxScrollTop = (scrollWindow) => {
  const doc = scrollWindow.document.documentElement
  return Math.max(0, doc.scrollHeight - scrollWindow.innerHeight)
}

// Identity token of the animation that owns the frame loop: a newer animation
// or a cancel replaces it and the orphaned loop stops on its next frame.
let activeAnimation = null

export const cancelScrollAnimation = () => {
  activeAnimation = null
}

const animateScrollTo = (scrollWindow, targetTop) => {
  // A new scroll always supersedes an in-flight one — including when it takes
  // the instant-jump path below, which would otherwise leave the old loop
  // running and dragging the viewport back toward its stale target.
  cancelScrollAnimation()

  const startTop = scrollWindow.scrollY
  const distance = targetTop - startTop

  if (prefersReducedMotion() || Math.abs(distance) < SKIP_DISTANCE_PX) {
    scrollWindow.scrollTo(0, Math.min(targetTop, maxScrollTop(scrollWindow)))
    return
  }

  const duration = Math.min(
    MAX_ANIMATION_MS,
    Math.max(MIN_ANIMATION_MS, Math.abs(distance) * ANIMATION_MS_PER_PX)
  )
  const token = {}
  activeAnimation = token

  // DOM events do not cross frame boundaries: a gesture over the widget's own
  // content fires in our window even though the host ends up scrolling, so
  // when the host window is the scroll target both windows must be watched.
  const cancelTargets = scrollWindow === window ? [ window ] : [ window, scrollWindow ]
  const stopListening = () =>
    cancelTargets.forEach((win) =>
      CANCEL_EVENTS.forEach((name) => win.removeEventListener(name, handleUserScroll)))
  const handleUserScroll = () => {
    if (activeAnimation === token) {
      activeAnimation = null
    }
    stopListening()
  }
  cancelTargets.forEach((win) =>
    CANCEL_EVENTS.forEach((name) => win.addEventListener(name, handleUserScroll, { passive: true })))

  const startedAt = performance.now()
  const step = (now) => {
    if (activeAnimation !== token) {
      stopListening()
      return
    }

    const progress = Math.min(1, (now - startedAt) / duration)
    const top = startTop + distance * easeInOutCubic(progress)
    scrollWindow.scrollTo(0, Math.min(top, maxScrollTop(scrollWindow)))

    if (progress < 1) {
      requestAnimationFrame(step)
    } else {
      activeAnimation = null
      stopListening()
    }
  }
  requestAnimationFrame(step)
}

// Scrolls so the element's top sits topRatio of the way down the display,
// leaving it the space below (0.25 leaves it the bottom three quarters; 0.5
// roughly centers a short row). scrollIntoView cannot do this from inside the
// height-synced iframe — it scrolls our own document, whose scroll range the
// host clamps away on its next resize — so the height is synced up front and
// the host window is scrolled directly.
//
// While something holds the page fullscreen (e.g. the leaderboard's rotate
// lock), the fullscreen element is the viewport and the top window cannot
// move it — there scrollIntoView is the right tool, since the scroll happens
// between the element and its own ancestors.
export const scrollToReveal = (element, { topRatio = DEFAULT_TOP_RATIO } = {}) => {
  if (!element) {
    return
  }

  if (fullscreenElement()) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    return
  }

  syncEmbedIframeHeight()

  const scrollWindow = hostGrowsIframe() && !isFixedHeightEmbed() ? hostWindow() : window
  const offsetTop = scrollWindow === window ? 0 : frameOffset().top
  const viewportTop = offsetTop + element.getBoundingClientRect().top
  const target = scrollWindow.scrollY + viewportTop - scrollWindow.innerHeight * topRatio

  animateScrollTo(scrollWindow, Math.max(0, target))
}
